import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, MenuItem, Modal, Paper, TextField, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PaymentIcon from "@mui/icons-material/Payment";
import PrintIcon from "@mui/icons-material/Print";
import api from "../../api";

const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const money = (value) => Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const modalStyle = { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: "90%", sm: 460 }, bgcolor: "background.paper", boxShadow: 24, p: 3, borderRadius: 2 };

const getStaffList = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data)) return data;
  return data?.staff || data?.teachers || data?.employees || payload?.staff || payload?.teachers || [];
};

const getMonthList = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data)) return data;
  return data?.months || data?.salary_months || data?.data || payload?.months || payload?.salary_months || [];
};

const getMonthNumber = (record) => {
  const value = record?.month_number ?? record?.month;
  const number = Number(value);
  if (number >= 1 && number <= 12) return number;
  const dateValue = record?.salary_month || record?.month_name || record?.month;
  const timestamp = dateValue && Date.parse(dateValue);
  return Number.isNaN(timestamp) ? 0 : new Date(timestamp).getMonth() + 1;
};
const getAcademicYearList = (payload) => {
  const data = payload?.data;
  if (Array.isArray(data)) return data;
  return data?.academic_years || data?.years || data?.sessions || data?.data || payload?.academic_years || payload?.years || payload?.sessions || [];
};
const getAcademicYearCalendarYear = (academicYear, fallbackYear) => {
  const value = academicYear?.start_date || academicYear?.startDate || academicYear?.start_year || academicYear?.session_name || academicYear?.name || academicYear?.year || "";
  const match = String(value).match(/(?:19|20)\d{2}/);
  return match ? Number(match[0]) : fallbackYear;
};
const toValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
const getAcademicYearYears = (academicYear) => {
  const value = academicYear?.session_name || academicYear?.name || academicYear?.year || "";
  const matches = String(value).match(/(?:19|20)\d{2}/g) || [];
  const startYear = Number(academicYear?.start_year || academicYear?.startYear || matches[0]) || null;
  const endYear = Number(academicYear?.end_year || academicYear?.endYear || matches[1]) || (startYear ? startYear + 1 : null);
  return { startYear, endYear };
};

const getStoredAcademicYearId = () => {
  const idKeys = [
    "academic_year_id", "academicYearId", "selected_academic_year_id",
    "selectedAcademicYearId", "current_academic_year_id", "currentAcademicYearId",
    "selected_year_id", "selectedYearId"
  ];
  for (const key of idKeys) {
    const value = localStorage.getItem(key);
    if (value !== null && String(value).trim() !== "") return String(value).trim();
  }
  const objectKeys = ["academic_year", "academicYear", "selected_academic_year", "selectedAcademicYear", "current_academic_year", "currentAcademicYear"];
  for (const key of objectKeys) {
    const value = localStorage.getItem(key);
    if (!value) continue;
    try {
      const parsed = JSON.parse(value);
      const id = parsed?.id ?? parsed?.academic_year_id ?? parsed?.academicYearId;
      if (id !== undefined && id !== null && String(id).trim() !== "") return String(id).trim();
    } catch {
      // Ignore non-JSON values.
    }
  }
  return "";
};

export default function StaffSalary() {
  const [staff, setStaff] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [monthStatus, setMonthStatus] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [academicYearId, setAcademicYearId] = useState(() => getStoredAcademicYearId());
  const [academicYearReady, setAcademicYearReady] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);
   
  const selectedStaff = useMemo(() => staff.find((item) => String(item.id) === String(selectedStaffId)), [staff, selectedStaffId]);
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const schoolId = localStorage.getItem("school_id") || localStorage.getItem("selected_school_id") || "";
  const selectedAcademicYear = academicYears.find((item) => String(item.id ?? item.academic_year_id ?? item.academicYearId) === String(academicYearId));
  const salaryYear = getAcademicYearCalendarYear(selectedAcademicYear, currentYear);
  const academicYearStatus = String(selectedAcademicYear?.status || "").toLowerCase();
  const academicYearStart = toValidDate(selectedAcademicYear?.start_date || selectedAcademicYear?.startDate || selectedAcademicYear?.start);
  const academicYearEnd = toValidDate(selectedAcademicYear?.end_date || selectedAcademicYear?.endDate || selectedAcademicYear?.end);
  const { startYear: academicYearStartYear, endYear: academicYearEndYear } = getAcademicYearYears(selectedAcademicYear);
  const todayStart = new Date(currentYear, currentMonth - 1, currentDate.getDate());
  const academicYearIsPast = academicYearStatus === "expired" || Boolean(academicYearEnd && academicYearEnd < todayStart) || Boolean(academicYearEndYear && academicYearEndYear < currentYear);
  const academicYearIsFuture = academicYearStatus === "upcoming" || Boolean(academicYearStart && academicYearStart > todayStart) || Boolean(academicYearStartYear && academicYearStartYear > currentYear);
  const isUpcomingMonth = (monthNumber) => !academicYearIsPast && (academicYearIsFuture || (salaryYear === currentYear && monthNumber > currentMonth) || salaryYear > currentYear);

  useEffect(() => {
    let active = true;
    const loadActiveAcademicYear = async () => {
      try {
        const response = await api.get("/school/academic_year/list", {
          params: { school_id: schoolId || undefined, _ts: Date.now() }
        });
        const years = getAcademicYearList(response.data);
        if (active) setAcademicYears(years);
        const storedId = getStoredAcademicYearId();
        const selected = years.find((item) => String(item.id ?? item.academic_year_id ?? item.academicYearId) === String(storedId)) || years.find((item) => String(item.status || "").toLowerCase() === "active") || years[0];
        const selectedId = selected?.id ?? selected?.academic_year_id ?? selected?.academicYearId;
        if (active && selectedId !== undefined && selectedId !== null && String(selectedId) !== String(storedId)) {
          setAcademicYearId(String(selectedId));
          localStorage.setItem("academic_year_id", String(selectedId));
        }
      } catch {
        // The salary endpoint can still load without an academic-year filter.
      } finally {
        if (active) setAcademicYearReady(true);
      }
    };
    loadActiveAcademicYear();
    return () => { active = false; };
  }, [schoolId]);

  useEffect(() => {
    const loadStaff = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await api.get("/teachers/list", { params: { school_id: schoolId || undefined, _ts: Date.now() } });
        setStaff(getStaffList(response.data));
        setSelectedStaffId("");
        setMonthStatus([]);
      } catch (err) {
        const status = err.response?.status ? ` (${err.response.status})` : "";
        setError(err.response?.data?.message || `${err.message || "Request failed"}${status}`);
      } finally {
        setLoading(false);
      }
    };
    loadStaff();
  }, [schoolId]);

  useEffect(() => {
    if (!selectedStaffId || !academicYearReady) return;
    const loadMonths = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await api.get("/salary/monthly-status", {
          params: { employee_id: selectedStaffId, year: salaryYear, academic_year_id: academicYearId || undefined, school_id: schoolId || undefined, _ts: Date.now() }
        });
        setMonthStatus(getMonthList(response.data));
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load salary months.");
        setMonthStatus([]);
      } finally {
        setLoading(false);
      }
    };
    loadMonths();
  }, [selectedStaffId, currentYear, salaryYear, academicYearId, academicYearReady, schoolId]);

  useEffect(() => {
    const idKeys = [
      "academic_year_id", "academicYearId", "selected_academic_year_id",
      "selectedAcademicYearId", "current_academic_year_id", "currentAcademicYearId",
      "selected_year_id", "selectedYearId"
    ];
    const updateAcademicYear = (event) => {
      const detail = event?.detail;
      const eventId = detail?.academicYearId ?? detail?.academic_year_id ?? detail?.academicYear?.id ?? detail?.academic_year?.id ?? detail?.id ?? (typeof detail === "string" || typeof detail === "number" ? detail : undefined);
      const nextId = String(eventId ?? getStoredAcademicYearId() ?? "").trim();
      setAcademicYearId((previous) => previous === nextId ? previous : nextId);
    };
    const handleStorage = (event) => {
      if (idKeys.includes(event.key) || event.key === null) updateAcademicYear(event);
    };
    const handleFocus = () => updateAcademicYear();
    ["academicYearChanged", "academic_year_changed", "academic-year-changed"].forEach((name) => window.addEventListener(name, updateAcademicYear));
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleFocus);
    return () => {
      ["academicYearChanged", "academic_year_changed", "academic-year-changed"].forEach((name) => window.removeEventListener(name, updateAcademicYear));
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const getRecord = (monthNumber) => monthStatus.find((item) => getMonthNumber(item) === monthNumber) || {};

  const getMonthStatus = (record, salary, paid, due) => {
    const status = String(record.status || record.payment_status || "").toLowerCase();
    if (status === "paid" || (salary > 0 && due <= 0 && paid > 0)) return "paid";
    if (status === "partial" || (paid > 0 && due > 0)) return "partial";
    return "unpaid";
  };

  const openPayment = (monthNumber) => {
    if (isUpcomingMonth(monthNumber)) return;
    const record = getRecord(monthNumber);
    setSelectedMonth(monthNumber);
    setAmount(record.closing_due || record.due || record.net_salary || record.total_salary || selectedStaff?.salary || "");
    setOpenModal(true);
  };

  const paySalary = async () => {
    if (!selectedMonth || isUpcomingMonth(selectedMonth)) {
      setError("Upcoming month salary cannot be paid yet.");
      return;
    }
    const salaryPaid = Number(amount);
    if (!salaryPaid || salaryPaid <= 0) {
      setError("Enter a valid payment amount.");
      return;
    }
    try {
      setSaving(true);
      const paymentResponse = await api.post("/salary/add", { employee_id: Number(selectedStaffId), salary_month: Number(selectedMonth), salary_paid: salaryPaid, adjustments: [], payment_date: paymentDate, note, academic_year_id: academicYearId || undefined, school_id: schoolId || undefined });
      if (paymentResponse.data?.status === false) throw new Error(paymentResponse.data?.message || "Payment failed.");
      setOpenModal(false);
      setNote("");
      const response = await api.get("/salary/monthly-status", {
        params: { employee_id: selectedStaffId, year: salaryYear, academic_year_id: academicYearId || undefined, school_id: schoolId || undefined, _ts: Date.now() }
      });
      setMonthStatus(getMonthList(response.data));
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Payment failed.");
    } finally {
      setSaving(false);
    }
  };

  const printSalary = async (monthNumber, thermal = false) => {
    try {
      const response = await api.get(`/salary-invoice/${selectedStaffId}/${monthNumber}`, {
        params: { academic_year_id: academicYearId || undefined, year: salaryYear, _ts: Date.now() }
      });
      if (!response.data?.status) throw new Error(response.data?.message || "Salary slip not found.");
      const data = response.data.data;
      const printWindow = window.open("", "_blank");
      if (!printWindow) throw new Error("Please allow popups to print the salary slip.");
      const width = thermal ? "70mm" : "100%";
      printWindow.document.write(`<html><head><title>Salary Pay Slip</title><style>@page{size:${thermal ? "80mm auto" : "A4"};margin:5mm}body{font-family:Arial,sans-serif;width:${width};margin:0 auto;color:#222}.copy{border:1px solid #ddd;padding:20px}.title{text-align:center;background:#000;color:#fff;padding:8px;font-weight:bold;margin:16px 0}.row{display:flex;justify-content:space-between;border-bottom:1px solid #ddd;padding:8px 0}.label{font-weight:bold}.total{font-size:20px;font-weight:bold;text-align:right;margin-top:18px}.footer{text-align:center;border-top:1px dashed #777;margin-top:28px;padding-top:10px;font-size:11px}</style></head><body><div class="copy"><h2 style="text-align:center">${data.school_name || "Salary Pay Slip"}</h2><div class="title">SALARY PAY SLIP - ${data.salary_month || months[monthNumber - 1]}</div><div class="row"><span class="label">Employee Name</span><span>${data.employee_name || selectedStaff?.name || "-"}</span></div><div class="row"><span class="label">Employee ID</span><span>${data.employee_id || selectedStaffId}</span></div><div class="row"><span class="label">Salary Month</span><span>${data.salary_month || months[monthNumber - 1]}</span></div><div class="row"><span class="label">Payment Date</span><span>${data.payment_date || paymentDate}</span></div><div class="row"><span class="label">Total Paid</span><span>Rs. ${money(data.total_paid || data.salary_paid)}</span></div><div class="row"><span class="label">Closing Due</span><span>Rs. ${money(data.closing_due)}</span></div><div class="total">Net Salary: Rs. ${money(data.net_salary || data.total_paid)}</div><div class="footer">This is a system generated salary slip.<br/>Authorized Signature</div></div><script>window.onload=function(){window.print();}</script></body></html>`);
      printWindow.document.close();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not print salary slip.");
    }
  };

  const staffName = selectedStaff?.name || selectedStaff?.full_name || "Selected Staff";
  const staffPhotoPath = selectedStaff?.photo || selectedStaff?.file || selectedStaff?.image || selectedStaff?.profile_photo || selectedStaff?.photo_url || "";
  const staffPhoto = staffPhotoPath && (/^https?:\/\//i.test(staffPhotoPath) ? staffPhotoPath : `https://dgps.co.in/${String(staffPhotoPath).replace(/^\/+/, "")}`);

  return <div style={{ padding: 20, background: "#fff", minHeight: "100%", fontSize: 17 }}>
    <Paper elevation={4} style={{ padding: 20, borderRadius: 12, background: "#f9f9f9", marginBottom: 10 }}><Grid container spacing={2}><Grid item xs={12} sm={7}><TextField select fullWidth label="Select Staff" value={selectedStaffId} disabled={loading} onChange={(event) => setSelectedStaffId(event.target.value)}><MenuItem value="">{loading ? "Loading staff..." : "Select Staff"}</MenuItem>{staff.map((item) => <MenuItem key={item.id} value={item.id}>{item.name || item.full_name} {item.designation ? `- ${item.designation}` : ""}</MenuItem>)}</TextField></Grid><Grid item xs={12} sm={5}><TextField select fullWidth label="Payment Status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><MenuItem value="all">All Months</MenuItem><MenuItem value="paid">Paid</MenuItem><MenuItem value="partial">Partial</MenuItem><MenuItem value="unpaid">Unpaid</MenuItem></TextField></Grid></Grid></Paper>
    {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
    <Grid container spacing={2}><Grid item xs={12} md={4}><Paper elevation={4} style={{ padding: 20, borderRadius: 12, background: "#f9f9f9" }}><Typography variant="h6" style={{ fontWeight: "bold", textAlign: "center", color: "#333", borderBottom: "2px solid #3f51b5", paddingBottom: 8, marginBottom: 10 }}>Staff Details</Typography>{selectedStaff ? <div style={{ display: "grid", gap: 8 }}>{staffPhoto ? <div style={{ display: "flex", justifyContent: "center", marginBottom: 15 }}><img src={staffPhoto} alt={staffName} onError={(event) => { event.currentTarget.style.display = "none"; }} style={{ width: 120, height: 120, borderRadius: "50%", objectFit: "cover", border: "2px solid #3f51b5" }} /></div> : <Typography style={{ textAlign: "center", color: "#888", fontStyle: "italic", marginBottom: 10 }}>No Photo Available</Typography>}<Typography><b>Name:</b> {staffName}</Typography><Typography><b>Designation:</b> {selectedStaff.designation || selectedStaff.department || "-"}</Typography><Typography><b>Staff ID:</b> {selectedStaff.id}</Typography><Typography><b>Mobile:</b> {selectedStaff.mobile || "-"}</Typography></div> : <Typography style={{ textAlign: "center", color: "#777", fontStyle: "italic" }}>No Staff Selected</Typography>}</Paper></Grid>
      <Grid item xs={12} md={8}><Paper elevation={4} style={{ padding: 20, borderRadius: 12, background: "#f9f9f9" }}><Typography variant="h6" style={{ fontWeight: "bold", textAlign: "center", color: "#333", borderBottom: "2px solid #3f51b5", paddingBottom: 8, marginBottom: 15 }}>Monthly Salary Details</Typography>{!selectedStaff ? <Typography style={{ textAlign: "center", color: "#777", fontStyle: "italic" }}>Select a staff member to view salary.</Typography> : <Grid container spacing={2}>{months.map((month, index) => { const monthNumber = index + 1; const upcoming = isUpcomingMonth(monthNumber); const record = getRecord(monthNumber); const paid = upcoming ? 0 : Number(record.total_paid ?? record.paid ?? record.salary_paid ?? 0); const salary = upcoming ? 0 : Number(record.net_salary ?? record.total_salary ?? record.salary ?? selectedStaff.salary ?? selectedStaff.monthly_salary ?? 0); const due = upcoming ? 0 : Number(record.closing_due ?? record.due ?? Math.max(salary - paid, 0)); const status = upcoming ? "upcoming" : getMonthStatus(record, salary, paid, due); const isPaid = status === "paid"; if (statusFilter !== "all" && status !== statusFilter) return null; return <Grid item xs={12} sm={6} md={4} key={month}><Paper elevation={2} style={{ padding: 20, borderRadius: 16, textAlign: "center", background: isPaid ? "linear-gradient(135deg, #e0f2f1, #e8f5e9)" : upcoming ? "#f3f4f6" : "#fff", border: isPaid ? "1px solid #81c784" : upcoming ? "1px dashed #9ca3af" : "1px solid #e0e0e0" }}><Typography style={{ fontWeight: 750, color: "#333", fontSize: 20 }}>{month}</Typography><Typography variant="h5" style={{ color: upcoming ? "#6b7280" : "#3f51b5", margin: "5px 0", fontSize: 28, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{upcoming ? "—" : `Rs. ${money(salary)}`}</Typography><Typography style={{ color: isPaid ? "#2e7d32" : upcoming ? "#6b7280" : status === "partial" ? "#ed6c02" : "#d32f2f", fontWeight: 750, margin: "8px 0", fontSize: 18 }}>{upcoming ? "Upcoming" : status === "paid" ? "Paid" : status === "partial" ? "Partial" : "Unpaid"}</Typography>{isPaid ? <><CheckCircleIcon style={{ color: "#2e7d32", fontSize: 32 }} /><div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12, flexWrap: "wrap" }}><Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => printSalary(monthNumber)} sx={{ fontSize: 15, fontWeight: 700 }}>Print</Button><Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => printSalary(monthNumber, true)} sx={{ fontSize: 15, fontWeight: 700 }}>Thermal Print</Button></div></> : upcoming ? <Typography variant="body2" style={{ color: "#6b7280", marginTop: 12, fontSize: 15 }}>Not payable yet</Typography> : <Button variant="contained" size="small" startIcon={<PaymentIcon />} onClick={() => openPayment(monthNumber)} style={{ marginTop: 12, fontSize: 15, fontWeight: 700 }}>Pay Now</Button>}</Paper></Grid>; })}</Grid>}</Paper></Grid></Grid>
    <Modal open={openModal} onClose={() => setOpenModal(false)}><Box sx={modalStyle}><Typography variant="h6" fontWeight="bold" mb={2}>Pay Salary - {months[(selectedMonth || 1) - 1]}</Typography><TextField fullWidth type="number" label="Payment Amount" value={amount} onChange={(event) => setAmount(event.target.value)} margin="normal" /><TextField fullWidth type="date" label="Payment Date" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} margin="normal" InputLabelProps={{ shrink: true }} /><TextField fullWidth label="Note" value={note} onChange={(event) => setNote(event.target.value)} margin="normal" /><Button fullWidth variant="contained" startIcon={<PaymentIcon />} onClick={paySalary} disabled={saving} sx={{ mt: 2 }}>{saving ? "Processing..." : "Confirm Payment"}</Button></Box></Modal>
  </div>;
}
